import { useState } from 'react';
import Button from './ui/Button';
import { submitFormPayload } from '../utils/submitForm';

const goalOptions = ['Plan retirement income', 'Optimize equity comp', 'Reduce taxes', 'Clarify estate plan', 'Other'];

export default function LeadMagnetForm({ className = '' }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    goal: goalOptions[0],
    consent: false
  });
  const [status, setStatus] = useState(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canSubmit = form.firstName.trim() && form.lastName.trim() && emailValid && form.consent;

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) {
      setStatus({ type: 'error', message: 'Please complete the form before submitting.' });
      return;
    }

    setStatus({ type: 'loading', message: 'Submitting…' });

    const response = await submitFormPayload({
      formName: 'Lead Magnet – 5 Step Check-Up Download',
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      comments: `Goal: ${form.goal}`
    });

    if (response?.success) {
      setStatus({ type: 'success', message: 'Thank you! Your guide is on the way to your inbox.' });
      setForm({ firstName: '', lastName: '', email: '', goal: goalOptions[0], consent: false });
    } else {
      console.error('Form submit failed', response);
      setStatus({ type: 'error', message: 'Something went wrong submitting your request. Please try again in a moment.' });
    }
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`.trim()} noValidate>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="lead-first-name" className="block text-sm font-medium text-cam-blue">
            First Name *
          </label>
          <input
            id="lead-first-name"
            name="firstName"
            type="text"
            value={form.firstName}
            onChange={handleChange}
            required
            placeholder="First name"
            className="mt-1 w-full rounded-xl border border-cam-blue/30 bg-white px-4 py-3 text-base md:text-sm text-slate-700 placeholder:text-slate-400 focus:border-cam-blue focus:outline-none focus:ring-2 focus:ring-cam-blue/40"
          />
        </div>
        <div>
          <label htmlFor="lead-last-name" className="block text-sm font-medium text-cam-blue">
            Last Name *
          </label>
          <input
            id="lead-last-name"
            name="lastName"
            type="text"
            value={form.lastName}
            onChange={handleChange}
            required
            placeholder="Last name"
            className="mt-1 w-full rounded-xl border border-cam-blue/30 bg-white px-4 py-3 text-base md:text-sm text-slate-700 placeholder:text-slate-400 focus:border-cam-blue focus:outline-none focus:ring-2 focus:ring-cam-blue/40"
          />
        </div>
      </div>
      <div>
        <label htmlFor="lead-email" className="block text-sm font-medium text-cam-blue">
          Email *
        </label>
        <input
          id="lead-email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          required
          placeholder="name@email.com"
          className="mt-1 w-full rounded-xl border border-cam-blue/30 bg-white px-4 py-3 text-base md:text-sm text-slate-700 placeholder:text-slate-400 focus:border-cam-blue focus:outline-none focus:ring-2 focus:ring-cam-blue/40"
        />
      </div>
      <div>
        <label htmlFor="lead-goal" className="block text-sm font-medium text-cam-blue">
          One goal for your finances
        </label>
        <select
          id="lead-goal"
          name="goal"
          value={form.goal}
          onChange={handleChange}
          className="mt-1 w-full rounded-xl border border-cam-blue/30 bg-white px-4 py-3 text-base md:text-sm text-slate-700 focus:border-cam-blue focus:outline-none focus:ring-2 focus:ring-cam-blue/40"
        >
          {goalOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-start gap-3">
        <input
          id="lead-consent"
          name="consent"
          type="checkbox"
          checked={form.consent}
          onChange={handleChange}
          className="mt-1 h-4 w-4 rounded border-cam-blue/40 text-cam-blue focus:ring-cam-blue"
          required
        />
        <label htmlFor="lead-consent" className="text-sm text-slate-600">
          I acknowledge the disclosures and understand this download is informational only.
        </label>
      </div>
      <Button
        type="submit"
        className="w-full focus:ring-offset-white"
        disabled={!canSubmit || status?.type === 'loading'}
      >
        {status?.type === 'loading' ? 'Submitting…' : 'Download “The 5-Step Check-Up”'}
      </Button>
      {status && (
        <p className={`text-base md:text-sm ${status.type === 'error' ? 'text-red-600' : 'text-cam-blue'}`} role="status">
          {status.message}
        </p>
      )}
    </form>
  );
}
